/**
 * Picks up a new deploy without anybody pressing anything.
 *
 * Cache headers get a fresh visitor the current build, but they do nothing for
 * a tab that has been open since before the deploy — and a voice channel is
 * exactly the kind of tab that stays open for hours. This polls for a change
 * and reloads on its own.
 *
 * The one thing it will not do is reload during a call. Dropping somebody
 * mid-sentence to apply an update is worse than the update arriving late, so a
 * pending reload waits for the call to end and goes through the moment it does.
 */

/** How often to ask whether the build changed. */
export const VERSION_POLL_MS = 60_000;

/**
 * Reads the current build's fingerprint.
 *
 * A `HEAD` on the page costs a couple of hundred bytes, against ~37 KB to
 * fetch it — and the `ETag` is enough, because `index.html` names the hashed
 * bundle inside it: a new build is a new bundle name is a new ETag.
 */
export async function fetchVersionTag(signal?: AbortSignal): Promise<string | null> {
  try {
    const response = await fetch('/', { method: 'HEAD', cache: 'no-store', signal });
    return response.headers.get('etag');
  } catch {
    // Offline, or the server is mid-restart. Silence is right: this runs on a
    // timer and a failed check simply becomes the next check.
    return null;
  }
}

export interface ReloadDecision {
  /** The fingerprint captured when the page loaded. */
  loadedTag: string | null;
  /** What the server reports now. */
  currentTag: string | null;
  /** Whether the person is in a voice channel right now. */
  inCall: boolean;
}

/**
 * Whether to reload right now.
 *
 * Pure so the rule can be tested without a browser, a timer, or a deploy.
 */
export function shouldReload({ loadedTag, currentTag, inCall }: ReloadDecision): boolean {
  if (loadedTag === null || currentTag === null) {
    return false;
  }

  return currentTag !== loadedTag && !inCall;
}

export interface WatchOptions {
  /** Answers whether a call is in progress, asked at each decision point. */
  isInCall: () => boolean;
  /** Performs the reload. Injected so tests can watch instead of navigate. */
  reload: () => void;
  intervalMs?: number;
}

/**
 * Starts watching for a new build. Returns the function that stops it.
 *
 * A version seen while a call is running is remembered rather than discarded,
 * and rechecked on the following tick — which is how the reload lands right
 * after the call ends instead of waiting for the next deploy.
 */
export function watchForNewVersion(options: WatchOptions): () => void {
  const { isInCall, reload, intervalMs = VERSION_POLL_MS } = options;

  let loadedTag: string | null = null;
  let pendingTag: string | null = null;
  let stopped = false;
  const controller = new AbortController();

  async function tick(): Promise<void> {
    if (stopped) {
      return;
    }

    const currentTag = pendingTag ?? (await fetchVersionTag(controller.signal));

    if (loadedTag === null) {
      loadedTag = currentTag;
      return;
    }

    if (currentTag !== null && currentTag !== loadedTag) {
      // Held rather than dropped: the call will end, and this is what makes
      // the update land then instead of a minute later.
      pendingTag = currentTag;
    }

    if (shouldReload({ loadedTag, currentTag: pendingTag ?? currentTag, inCall: isInCall() })) {
      reload();
    }
  }

  void tick();
  const timer = setInterval(() => void tick(), intervalMs);

  return () => {
    stopped = true;
    controller.abort();
    clearInterval(timer);
  };
}
